import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Youtube, Save, Search, Loader2 } from 'lucide-react';
import useSongEmbeddability from '@/lib/useSongEmbeddability';

/**
 * Admin panel — lists every song whose main YouTube video is not embeddable
 * (preflight failure) AND that doesn't have `video_url_alt` set yet.
 * For each row we surface a one-click YouTube search (karaoké / instrumental)
 * and a paste-and-save input.
 */
export default function AltVideosPanel({ songs, onSaved }) {
  const embedMap = useSongEmbeddability(songs);
  const [drafts, setDrafts] = useState({}); // songId → altUrl
  const [savingId, setSavingId] = useState(null);

  // Only surface songs that (a) rely on YT and (b) preflight says "métronome" and (c) no alt already.
  const rows = useMemo(() => {
    return songs.filter((s) => {
      if (s.audio_url) return false;
      if (s.video_url_alt) return false;
      if (!s.video_url) return false;
      return embedMap[s.id] === 'metronome';
    });
  }, [songs, embedMap]);

  const checking = songs.some((s) => embedMap[s.id] === 'unknown');

  const save = async (song) => {
    const alt = (drafts[song.id] || '').trim();
    if (!alt) { toast.error('Colle une URL YouTube d\'abord'); return; }
    setSavingId(song.id);
    try {
      // Convert youtube.com/watch?v=X or youtu.be/X → embed form
      const normalized = alt
        .replace(/^https?:\/\/(www\.)?youtube\.com\/watch\?v=([A-Za-z0-9_-]+).*/, 'https://www.youtube.com/embed/$2')
        .replace(/^https?:\/\/youtu\.be\/([A-Za-z0-9_-]+).*/, 'https://www.youtube.com/embed/$1');
      await api.put(`/songs/${song.id}`, { ...song, video_url_alt: normalized });
      toast.success(`Vidéo alt. enregistrée pour ${song.title}`);
      setDrafts((d) => { const c = { ...d }; delete c[song.id]; return c; });
      onSaved?.();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
      console.debug(err);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div data-testid="admin-alt-videos-panel" className="space-y-4">
      <div className="card-stone p-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-orange-950/60 border border-orange-800/60 flex items-center justify-center text-orange-300">
            <Youtube size={20} />
          </div>
          <div className="flex-1">
            <div className="overline text-orange-400 mb-1">Vidéos alternatives — outil de correction en masse</div>
            <p className="text-sm text-stone-300 leading-relaxed">
              Certaines vidéos officielles (Wonderwall, Smoke on the Water…) ne peuvent pas être intégrées dans Maestro à cause des droits d'auteur.
              Cet outil détecte automatiquement ces chansons et te permet de les remplacer par des versions <span className="text-stone-100">karaoké / live / cover</span> embarquables, en un clic.
            </p>
            <div className="mt-3 text-xs text-stone-400">
              {checking ? (
                <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Vérification de {songs.length} chansons…</span>
              ) : (
                <span data-testid="admin-alt-count">{rows.length} chanson{rows.length > 1 ? 's' : ''} à corriger sur {songs.length}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {rows.length === 0 && !checking && (
        <div className="card-stone p-10 text-center text-stone-400">
          🎉 Toutes les chansons ont un embed YouTube fonctionnel ou une vidéo alternative déjà renseignée.
        </div>
      )}

      {rows.map((s, i) => {
        const query = `${s.title} ${s.artist} karaoke instrumental`;
        const ytSearch = `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
        return (
          <div key={s.id} data-testid={`admin-alt-row-${i}`} className="card-stone p-4 grid md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-4 min-w-0">
              <div className="text-stone-100 font-medium truncate">{s.title}</div>
              <div className="text-stone-400 text-sm truncate">{s.artist}</div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400 mt-1">Métronome seul</div>
            </div>
            <div className="md:col-span-2">
              <a
                href={ytSearch}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`admin-alt-search-${i}`}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full bg-emerald-900/40 hover:bg-emerald-900/60 border border-emerald-800/60 text-emerald-200 transition-colors w-full justify-center"
              >
                <Search size={11} /> Chercher karaoké
              </a>
            </div>
            <div className="md:col-span-4">
              <input
                type="url"
                value={drafts[s.id] || ''}
                onChange={(e) => setDrafts({ ...drafts, [s.id]: e.target.value })}
                placeholder="Colle l'URL YouTube trouvée…"
                className="input-stone text-sm"
                data-testid={`admin-alt-input-${i}`}
              />
            </div>
            <div className="md:col-span-2">
              <button
                onClick={() => save(s)}
                disabled={savingId === s.id || !drafts[s.id]}
                data-testid={`admin-alt-save-${i}`}
                className="btn-primary text-xs px-3 py-2 inline-flex items-center gap-1.5 w-full justify-center disabled:opacity-40"
              >
                {savingId === s.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Enregistrer
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
